import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContextMenu from '../Overlays/ContextMenu';
import InlineEditor from '../ProjectPanel/InlineEditor';
import { getVersions, getVersionDisplay, renameVersion, setVersionProtect } from '../../helpers/versionHelpers';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import Icon from '../Icon';

export default function VersionPanel({
  fixed,
  className,
  projectId,
  projectName,
  activeDoc,
  refreshToken = 0,
  tabs = [],
  leftActiveTabId,
  rightActiveTabId,
  activePane = 'left',
  versionsPanelOpen,
  onOpenVersion,
  onDeleteVersion,
  onRestoreVersion,
  onRestoreVersionCopy
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openingVersionId, setOpeningVersionId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const abortRef = useRef(null);
  const menuRefs = useRef({});
  const previousDocIdRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!versionsPanelOpen) return undefined;
    if (!projectId || !activeDoc?.id) {
      setVersions([]);
      setError(null);
      return undefined;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    getVersions({
      projectId,
      docId: activeDoc.id,
      signal: controller.signal
    })
      .then(list => setVersions(list))
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setError(err?.message || 'Failed to load versions.');
      })
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [projectId, activeDoc?.id, versionsPanelOpen, refreshToken]);

  const handleOpenVersionClick = async (version) => {
    if (!onOpenVersion || !version) return;
    try {
      setOpeningVersionId(version.versionId);
      const beh = computeOpenBehavior({ type: 'version', tabs, activePane, leftActiveTabId, rightActiveTabId });
      const toSend = {
        ...version,
        sourceDocId: activeDoc?.id,
        sourceDocName: activeDoc?.name,
        preferredPane: beh?.preferredPane || undefined
      };
      await onOpenVersion(toSend);
    } catch (err) {
      setError(err?.message || 'Unable to open version.');
    } finally {
      setOpeningVersionId(null);
    }
  };

  const handleRestoreVersionClick = async (version, mode = 'replace') => {
    if (!version) return;
    const action = mode === 'copy' ? onRestoreVersionCopy : onRestoreVersion;
    if (!action) return;

    try {
      setOpeningVersionId(version.versionId);
      await action({
        ...version,
        sourceDocId: activeDoc?.id,
        sourceDocName: activeDoc?.name
      });
    } catch (err) {
      setError(err?.message || 'Unable to restore version.');
    } finally {
      setOpeningVersionId(null);
    }
  };

  const handleDeleteVersionClick = async (version) => {
    if (!version || !onDeleteVersion) return;
    try {
      setOpeningVersionId(version.versionId);
      await onDeleteVersion({
        ...version,
        sourceDocId: activeDoc?.id,
        sourceDocName: activeDoc?.name
      });
      setVersions(prev => prev.filter(item => item.versionId !== version.versionId));
    } catch (err) {
      setError(err?.message || 'Unable to delete version.');
    } finally {
      setOpeningVersionId(null);
    }
  };

  const handleRenameVersionClick = async (version, nextName) => {
    if (!version?.versionId) return;
    const trimmed = (nextName || '').trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    try {
      await renameVersion({ versionId: version.versionId, name: trimmed });
      setVersions(prev => prev.map(v => (
        v.versionId === version.versionId
          ? { ...v, name: trimmed }
          : v
      )));
    } catch (err) {
      setError(err?.message || 'Unable to rename version.');
    } finally {
      setEditingId(null);
    }
  };

  const handleToggleProtectClick = async (version) => {
    if (!version?.versionId) return;
    const currentlyProtected = Boolean(version?.protected);
    try {
      setOpeningVersionId(version.versionId);
      await setVersionProtect({ versionId: version.versionId, protect: !currentlyProtected });

      // Defer pruning to the next createVersion cycle.
      // This keeps the unprotected version visible until a new save triggers prune.
      setVersions(prev => prev.map(v => (
        v.versionId === version.versionId
          ? { ...v, protected: !currentlyProtected }
          : v
      )));
    } catch (err) {
      setError(err?.message || 'Unable to update delete protection.');
    } finally {
      setOpeningVersionId(null);
    }
  };

  const groupedTree = useMemo(() => {
    const enriched = versions
      .map(version => {
        const display = getVersionDisplay(version);
        return {
          ...version,
          parsedDate: display.parsedDate,
          timestamp: display.timestamp,
          year: display.year,
          monthIndex: display.monthIndex,
          monthName: display.monthName,
          label: version?.name || display.label
        };
      })
      .filter(entry => Boolean(entry.parsedDate))
      .sort((a, b) => b.timestamp - a.timestamp);

    const yearMap = new Map();
    enriched.forEach(entry => {
      const yearKey = entry.year;
      const monthKey = `${entry.year}-${String((entry.monthIndex ?? 0) + 1).padStart(2, '0')}`;
      if (!yearMap.has(yearKey)) {
        yearMap.set(yearKey, new Map());
      }
      const monthMap = yearMap.get(yearKey);
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, {
          monthKey,
          monthIndex: entry.monthIndex,
          monthName: entry.monthName,
          items: []
        });
      }
      monthMap.get(monthKey).items.push(entry);
    });

    const years = Array.from(yearMap.keys())
      .sort((a, b) => Number(b) - Number(a))
      .map(yearKey => {
        const months = Array.from(yearMap.get(yearKey).values())
          .sort((a, b) => (b.monthIndex ?? 0) - (a.monthIndex ?? 0))
          .map(month => ({
            id: `version-month-${month.monthKey}`,
            type: 'folder',
            name: month.monthName,
            children: month.items.map(entry => ({
              id: `version-doc-${entry.versionId}`,
              type: 'version',
              name: entry.label,
              version: entry
            }))
          }));

        return {
          id: `version-year-${yearKey}`,
          type: 'folder',
          name: yearKey,
          children: months
        };
      });

    const latest = enriched[0] || null;
    const defaultOpenIds = [];
    if (latest?.year) {
      const yearId = `version-year-${latest.year}`;
      const monthId = `version-month-${latest.year}-${String((latest.monthIndex ?? 0) + 1).padStart(2, '0')}`;
      defaultOpenIds.push(yearId, monthId);
    }

    return { nodes: years, defaultOpenIds };
  }, [versions]);

  useEffect(() => {
    const docId = activeDoc?.id || null;
    const isDocChanged = previousDocIdRef.current !== docId;
    previousDocIdRef.current = docId;

    const validFolderIds = new Set();
    const collectFolderIds = (nodes = []) => {
      nodes.forEach(node => {
        if (node.type === 'folder') {
          validFolderIds.add(node.id);
          collectFolderIds(node.children || []);
        }
      });
    };
    collectFolderIds(groupedTree.nodes);

    const defaultSet = new Set(groupedTree.defaultOpenIds.filter(Boolean));
    setExpandedFolders(prev => {
      if (isDocChanged) return defaultSet;
      const next = new Set();
      prev.forEach(id => {
        if (validFolderIds.has(id)) next.add(id);
      });
      if (next.size === 0 && defaultSet.size > 0) {
        return defaultSet;
      }
      return next;
    });
  }, [activeDoc?.id, groupedTree]);

  const focusedTabId = activePane === 'right' ? rightActiveTabId : leftActiveTabId;

  const getOpenTabForVersion = (version) => (
    tabs.find(tab =>
      tab?.isVersion &&
      tab?.sourceDocId === activeDoc?.id &&
      tab?.versionId === version?.versionId
    )
  );

  const isVersionActive = (version) => {
    const openTab = getOpenTabForVersion(version);
    if (!openTab) return false;
    return openTab.id === leftActiveTabId || openTab.id === rightActiveTabId;
  };

  const isVersionOpen = (version) => Boolean(getOpenTabForVersion(version));

  const isVersionFocused = (version) => {
    const openTab = getOpenTabForVersion(version);
    return openTab?.id === focusedTabId;
  };

  const hasOpen = (node) => {
    if (!node) return false;
    if (node.type === 'version') return isVersionOpen(node.version);
    return (node.children || []).some(child => hasOpen(child));
  };

  const hasActive = (node) => {
    if (!node) return false;
    if (node.type === 'version') return isVersionActive(node.version);
    return (node.children || []).some(child => hasActive(child));
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const ensureMenuRef = (id) => {
    if (!menuRefs.current[id]) {
      menuRefs.current[id] = React.createRef();
    }
    return menuRefs.current[id];
  };

  const handleVersionDragStart = (event, version) => {
    if (!event?.dataTransfer || !version) return;
    const display = getVersionDisplay(version);
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/ghost', JSON.stringify({
      id: String(version.versionId || ''),
      kind: 'version'
    }));
    event.dataTransfer.setData('text/plain', version.versionId);
    event.dataTransfer.setData('application/x-ghost-version', JSON.stringify({
      projectId,
      docId: activeDoc?.id,
      versionId: version.versionId,
      label: display.label
    }));
  };

  const renderTree = (nodes = []) => {
    if (!nodes.length) return null;
    return nodes.map(node => {
      const isFolder = node.type === 'folder';
      const isExpanded = isFolder ? expandedFolders.has(node.id) : false;
      const nodeHasOpen = isFolder ? hasOpen(node) : false;
      const nodeHasActive = isFolder ? hasActive(node) : false;
      const isActiveTab = !isFolder && isVersionActive(node.version);
      const isOpenTab = !isFolder && isVersionOpen(node.version);
      const isFocused = !isFolder && isVersionFocused(node.version);
      const isEditing = editingId === node.id;
      const menuRef = ensureMenuRef(node.id);

      return (
        <li
          key={node.id}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
            if (isFolder && !isExpanded) {
              setExpandedFolders(prev => {
                const next = new Set(prev);
                next.add(node.id);
                return next;
              });
            }
          }}
        >
          <div
            className={`projectItem ${isFolder ? 'folder' : ''} ${isActiveTab ? 'active' : ''} ${isOpenTab && !isActiveTab ? 'open' : ''} ${isFolder && nodeHasOpen ? 'has-open' : ''} ${isFolder && nodeHasActive ? 'has-active' : ''} ${isFocused ? 'focused' : ''}`}
            id={node.id}
            draggable={!isFolder}
            onDragStart={e => {
              if (!isFolder) handleVersionDragStart(e, node.version);
            }}
            onClick={() => {
              if (isFolder) toggleFolder(node.id);
              else handleOpenVersionClick(node.version);
            }}
            data-open={isFolder ? (isExpanded ? 'open' : 'closed') : undefined}
            data-tooltip={node.name || 'Version'}
            data-tooltip-placement="bottom"
          >
            {isFolder ? (
              <Icon icon={isExpanded ? 'folder-open' : 'folder'} className={`icon ${isExpanded ? 'folder-open' : 'folder'}`} />
            ) : (
              <Icon icon={node?.version?.protected ? 'lock' : 'clock-counter-clockwise'} className={`icon ${node?.version?.protected ? 'lock' : 'clock-counter-clockwise'}`} />
            )}

            {isEditing ? (
              <InlineEditor
                value={editName}
                onChange={setEditName}
                onSave={() => handleRenameVersionClick(node.version, editName)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <span>{node.name}</span>
            )}

            {openingVersionId === node?.version?.versionId && (
              <span className="versionLoading">Openingâ€¦</span>
            )}

            <button
              ref={menuRef}
              className="iconButton"
              data-tooltip-block-parent="true"
              onClick={e => {
                e.stopPropagation();
                setMenuOpenId(menuOpenId === node.id ? null : node.id);
              }}
              aria-label="Version actions"
            >
              <Icon icon={'dots-three-vertical'} className="icon dots-three-vertical" />
            </button>

            {menuOpenId === node.id && (
              <ContextMenu
                triggerRef={menuRef}
                onClose={() => setMenuOpenId(null)}
                items={
                  isFolder
                    ? [
                        {
                          label: isExpanded ? 'Collapse' : 'Expand',
                          iconClass: isExpanded ? 'ph-folder-notch-minus' : 'ph-folder-notch-plus',
                          onClick: () => toggleFolder(node.id)
                        }
                      ]
                    : [
                        {
                          label: 'Open version',
                          iconClass: 'arrow-square-out',
                          onClick: () => handleOpenVersionClick(node.version)
                        },
                        {
                          label: 'Restore',
                          iconClass: 'git-merge',
                          onClick: () => handleRestoreVersionClick(node.version, 'replace')
                        },
                        {
                          label: 'Restore as a copy',
                          iconClass: 'git-fork',
                          onClick: () => handleRestoreVersionClick(node.version, 'copy')
                        },
                        {
                          label: 'Rename',
                          iconClass: 'pencil-simple',
                          onClick: () => {
                            setEditingId(node.id);
                            setEditName(node.name || 'Version');
                          }
                        },
                        {
                          label: node?.version?.protected ? 'Unprotect version' : 'Protect version',
                          iconClass: node?.version?.protected ? 'ph-lock-open' : 'ph-lock',
                          onClick: () => handleToggleProtectClick(node.version)
                        },
                        {
                          label: 'Delete version',
                          iconClass: 'trash',
                          onClick: () => handleDeleteVersionClick(node.version)
                        }
                      ]
                }
              />
            )}
          </div>

          {isFolder && isExpanded && node.children?.length > 0 && (
            <ul className="subList">{renderTree(node.children)}</ul>
          )}
        </li>
      );
    });
  };

  const renderContent = () => {
    if (!projectId) {
      return <p className="placeholder">Open a project to see saved versions.</p>;
    }

    if (!activeDoc?.id) {
      return <p className="placeholder">Select a document to view saved versions.</p>;
    }

    if (loading) {
      return <p className="placeholder">Loading versions...</p>;
    }

    if (error) {
      return <p className="placeholder">{error}</p>;
    }

    if (groupedTree.nodes.length === 0) {
      return <p className="placeholder">No versions saved yet for this document.</p>;
    }

    return (
      <div className="versionList">
        <ul>{renderTree(groupedTree.nodes)}</ul>
      </div>
    );
  };

  return (
    <div id="versionsPanel" className={`${fixed ? 'fixed' : 'overlay'} ${className || ''}`.trim()}>
      <div id="versionsHeader">
        <div className="projectName">
          <button
            type="button"
            className="iconButton"
            aria-label="Back to projects"
            onClick={() => navigate('/projects')}
          >
            <Icon icon={'caret-left'} className="icon caret-left" />
          </button>
          <h3>{projectName || 'Untitled Project'}</h3>
        </div>

        <div className="projectActions">
          <span className="versionActionsDocName">{activeDoc?.name || 'Untitled document'}</span>
        </div>
      </div>
      <div id="versionsContent">
        <div className="projectListHeader">
          <span className="projectListTitle">All versions</span>
        </div>
        {renderContent()}
      </div>
    </div>
  );
}

